import { authenticatedRequest } from './authApi';
import { REST_API_ORIGIN } from '../platform/endpoints';
import { localizedValue } from '../localization/presentationLanguage';
const interpretationText = (es, en) => localizedValue({ es, en });

const API_BASE = REST_API_ORIGIN;

const BASE_PATH = `${API_BASE}/api/synastry/interpretation`;

async function parseJsonSafe(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { ok: false, error: interpretationText('Respuesta inválida del servidor', 'Invalid server response'), raw: text };
  }
}

async function request(path, options = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20000);

  try {
    const response = await authenticatedRequest(`${BASE_PATH}${path}`, {
      ...options,
      rawResponse: true,
      signal: controller.signal,
      diagSource: 'synastryInterpretationApi',
    });
    const payload = await parseJsonSafe(response);

    if (!response.ok || payload?.ok === false) {
      const error = new Error(
        payload?.error ||
        payload?.message ||
        interpretationText('No se pudo procesar la interpretación guiada', 'The guided interpretation could not be processed.')
      );
      error.status = response.status;
      error.payload = payload;
      throw error;
    }

    return payload;
  } finally {
    clearTimeout(timeoutId);
  }
}

function ensureReading(reading) {
  if (!reading || typeof reading !== 'object') {
    throw new Error(interpretationText('Falta la lectura de sinastría para la interpretación guiada.', 'The synastry reading is missing for guided interpretation.'));
  }
}

function ensureQuestion(question) {
  if (!question || typeof question !== 'object') {
    throw new Error(interpretationText('Falta la pregunta guiada a interpretar.', 'The guided question is missing.'));
  }

  if (!String(question.id || '').trim()) {
    throw new Error(interpretationText('La pregunta guiada no tiene un id válido.', 'The guided question does not have a valid ID.'));
  }
}

export async function getGuidedQuestions(reading) {
  ensureReading(reading);

  return request('/questions', {
    method: 'POST',
    body: { reading },
  });
}

export async function requestGuidedInterpretation({
  reading,
  question,
  readingFingerprint = '',
}) {
  ensureReading(reading);
  ensureQuestion(question);

  return request('/interpret', {
    method: 'POST',
    body: {
      reading,
      question,
      readingFingerprint: String(readingFingerprint || '').trim() || undefined,
    },
  });
}

export async function requestFreeQuestionInterpretation({
  reading,
  freeQuestion,
  readingFingerprint = '',
  previousContext = null,
}) {
  ensureReading(reading);

  const questionText = String(freeQuestion || '').trim();
  if (!questionText) {
    throw new Error(interpretationText('La pregunta libre no es válida.', 'The free-form question is invalid.'));
  }

  return request('/free', {
    method: 'POST',
    body: {
      reading,
      freeQuestion: questionText,
      readingFingerprint: String(readingFingerprint || '').trim() || undefined,
      previousContext:
        previousContext && typeof previousContext === 'object'
          ? {
              question: String(previousContext?.question || '').trim(),
              answer: String(previousContext?.answer || '').trim(),
            }
          : undefined,
    },
  });
}

const synastryInterpretationApi = {
  getGuidedQuestions,
  requestGuidedInterpretation,
  requestFreeQuestionInterpretation,
};

export default synastryInterpretationApi;
